/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/react-css
 * @description Renders the vanilla CSS adapter configuration form for the React
 * adapter package. This form lets consumers choose a provider and supply the
 * credentials required to persist a domain-specific adapter configuration.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import { DOMAIN_LABELS, getProvidersForDomain } from "@geenius/adapters";
import type { FormEvent, JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { cx } from "../lib/cx";

interface ProviderOptionField {
  key: string;
  label: string;
  description: string;
  placeholder?: string;
  inputMode?: "text" | "numeric";
}

const PROVIDER_OPTION_FIELDS: Partial<
  Record<AdapterDomain, Record<string, ProviderOptionField[]>>
> = {
  db: {
    convex: [
      {
        key: "deployment",
        label: "Deployment",
        description: "Convex deployment or project slug.",
        placeholder: "team:project",
      },
    ],
    neon: [
      {
        key: "database",
        label: "Database",
        description: "Database name or branch label for downstream wiring.",
        placeholder: "main",
      },
    ],
    cloudflareKV: [
      {
        key: "namespace",
        label: "KV Namespace",
        description: "Worker binding name used by the downstream KV adapter.",
        placeholder: "ADAPTERS_KV",
      },
      {
        key: "defaultTtlSeconds",
        label: "Default TTL Seconds",
        description: "Optional TTL for cache/session style records.",
        placeholder: "300",
        inputMode: "numeric",
      },
    ],
    memory: [
      {
        key: "seed",
        label: "Fixture Seed",
        description: "Optional deterministic seed for local test data.",
        placeholder: "demo",
      },
    ],
  },
};

const LAUNCH_DB_PROVIDER_IDS = new Set([
  "convex",
  "neon",
  "cloudflareKV",
  "memory",
]);

function getSelectableProviders(domain: AdapterDomain) {
  const providers = getProvidersForDomain(domain);
  if (domain !== "db") {
    return providers;
  }

  return providers.filter((entry) => {
    if (entry.id === "localStorage") {
      return true;
    }
    return LAUNCH_DB_PROVIDER_IDS.has(entry.id);
  });
}

function getOptionFields(
  domain: AdapterDomain,
  provider: string,
): ProviderOptionField[] {
  return PROVIDER_OPTION_FIELDS[domain]?.[provider] ?? [];
}

function stringifyOptionValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value);
}

function coerceOptionValue(field: ProviderOptionField, value: string): unknown {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "";
  }

  if (field.inputMode !== "numeric") {
    return trimmed;
  }

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : trimmed;
}

function validateOptionValues(
  domain: AdapterDomain,
  provider: string,
  optionValues: Record<string, string>,
): string | null {
  // Domain-specific checks first so they produce the most targeted message.
  if (domain === "db" && provider === "cloudflareKV") {
    if (!optionValues.namespace?.trim()) {
      return "KV namespace is required";
    }

    const ttlValue = optionValues.defaultTtlSeconds?.trim();
    if (ttlValue) {
      const ttlSeconds = Number(ttlValue);
      if (!Number.isInteger(ttlSeconds) || ttlSeconds <= 0) {
        return "Default TTL seconds must be a positive integer";
      }
    }
  }

  // Generic numeric-field guard: any provider-option field declared with
  // inputMode "numeric" must parse as a finite number when set. Without this
  // check, coerceOptionValue silently returns the raw string for non-numeric
  // input (e.g. "abc"), which then ships as part of config.options.
  for (const field of getOptionFields(domain, provider)) {
    if (field.inputMode !== "numeric") continue;
    const raw = optionValues[field.key]?.trim();
    if (!raw) continue;
    if (!Number.isFinite(Number(raw))) {
      return `${field.label} must be a number`;
    }
  }

  return null;
}

/**
 * Props accepted by the React CSS adapter configuration form.
 *
 * @property domain Adapter domain being configured in the current dialog
 * @property initialConfig Existing configuration values used to hydrate the form
 * @property onSave Async or sync handler that persists the submitted configuration
 * @property onCancel Optional callback invoked when the user dismisses the form
 * @property className Optional class name merged onto the form root
 * @property titleId Optional identifier applied to the form title for dialog labelling
 */
export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  onCancel?: () => void;
  className?: string;
  titleId?: string;
}

/**
 * Renders the adapter configuration workflow for a single adapter domain.
 *
 * @param props Component props controlling the selected domain and form callbacks
 * @returns A provider chooser and credential form styled with the CSS variant
 * @example
 * ```tsx
 * <AdapterConfigForm
 *   domain="auth"
 *   onSave={async (config) => saveConfig('auth', config)}
 *   onCancel={() => setOpen(false)}
 * />
 * ```
 */
export function AdapterConfigForm({
  domain,
  initialConfig,
  onSave,
  onCancel,
  className = "",
  titleId,
}: AdapterConfigFormProps): JSX.Element {
  const providers = useMemo(() => getSelectableProviders(domain), [domain]);
  const initialProvider =
    providers.find((entry) => entry.id === initialConfig?.provider)?.id ??
    providers[0]?.id ??
    "";
  const [provider, setProvider] = useState(initialProvider);
  const [apiKey, setApiKey] = useState(initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = useState(initialConfig?.baseUrl ?? "");
  const [optionValues, setOptionValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(initialConfig?.options ?? {}).map(([key, value]) => [
        key,
        stringifyOptionValue(value),
      ]),
    ),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedMeta = providers.find((entry) => entry.id === provider);
  const needsApiKey = selectedMeta?.requiresApiKey ?? false;
  const needsBaseUrl = selectedMeta?.requiresBaseUrl ?? false;
  const optionFields = getOptionFields(domain, provider);
  const errorId = `gn-adapters-${domain}-error`;
  const apiKeyHasError = error === "API key is required";
  const baseUrlHasError = error === "Base URL is required";
  const optionFieldHasError = (field: ProviderOptionField): boolean => {
    if (!error) return false;
    if (
      domain === "db" &&
      provider === "cloudflareKV" &&
      field.key === "namespace" &&
      error === "KV namespace is required"
    ) {
      return true;
    }
    if (
      domain === "db" &&
      provider === "cloudflareKV" &&
      field.key === "defaultTtlSeconds" &&
      error === "Default TTL seconds must be a positive integer"
    ) {
      return true;
    }
    if (error === `${field.label} must be a number`) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!needsApiKey && apiKey) {
      setApiKey("");
    }
    if (!needsBaseUrl && baseUrl) {
      setBaseUrl("");
    }
  }, [apiKey, baseUrl, needsApiKey, needsBaseUrl]);

  // Resynchronise form state when the caller swaps `domain` or `initialConfig`
  // on a mounted form. Without this, re-rendering from db→ai keeps the prior
  // db provider in state and `handleSubmit` would persist it for the new
  // domain. Also guards against a current provider that is no longer in the
  // selectable list for the new domain.
  useEffect(() => {
    const candidate = initialConfig?.provider;
    const nextProvider =
      providers.find((entry) => entry.id === candidate)?.id ??
      providers[0]?.id ??
      "";
    setProvider(nextProvider);
    setApiKey(initialConfig?.apiKey ?? "");
    setBaseUrl(initialConfig?.baseUrl ?? "");
    setOptionValues(
      Object.fromEntries(
        Object.entries(initialConfig?.options ?? {}).map(([key, value]) => [
          key,
          stringifyOptionValue(value),
        ]),
      ),
    );
    setError(null);
  }, [providers, initialConfig]);

  const handleProviderChange = (nextProvider: string): void => {
    if (nextProvider === provider) {
      return;
    }

    setProvider(nextProvider);
    setApiKey("");
    setBaseUrl("");
    setOptionValues({});
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!provider) {
      setError("Select a provider");
      return;
    }

    // Defensive: reject providers no longer selectable for the current domain.
    // This is the second guard against a stale provider surviving a domain
    // change between render and submit.
    if (!providers.some((entry) => entry.id === provider)) {
      setError("Select a provider");
      return;
    }

    if (needsApiKey && !apiKey.trim()) {
      setError("API key is required");
      return;
    }

    if (needsBaseUrl && !baseUrl.trim()) {
      setError("Base URL is required");
      return;
    }

    const optionError = validateOptionValues(domain, provider, optionValues);
    if (optionError) {
      setError(optionError);
      return;
    }

    setIsSaving(true);
    try {
      const config: DomainAdapterConfig = { provider };
      if (needsApiKey && apiKey.trim()) {
        config.apiKey = apiKey.trim();
      }
      if (needsBaseUrl && baseUrl.trim()) {
        config.baseUrl = baseUrl.trim();
      }

      const options = Object.fromEntries(
        optionFields
          .map((field) => [
            field.key,
            coerceOptionValue(field, optionValues[field.key] ?? ""),
          ])
          .filter(([, value]) => value !== ""),
      );
      if (Object.keys(options).length > 0) {
        config.options = options;
      }

      await onSave(config);
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : "Failed to save",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cx("gn-adapters-form", className)}
      noValidate
    >
      <h3 id={titleId} className="gn-adapters-form__title">
        Configure {DOMAIN_LABELS[domain]}
      </h3>
      <p className="gn-adapters-form__subtitle">
        Select and configure your preferred provider.
      </p>
      {error && (
        <div
          id={errorId}
          className="gn-adapters-alert gn-adapters-alert--error gn-adapters-alert--spaced"
          role="alert"
        >
          {error}
        </div>
      )}
      <fieldset className="gn-adapters-field gn-adapters-field--provider">
        <legend className="gn-adapters-field__label">Provider</legend>
        <div className="gn-adapters-provider-grid">
          {providers.map((p) => (
            <label
              key={p.id}
              className={cx(
                "gn-adapters-provider-option",
                provider === p.id && "gn-adapters-provider-option--selected",
              )}
            >
              <input
                type="radio"
                name={`gn-adapters-${domain}-provider`}
                value={p.id}
                checked={provider === p.id}
                onChange={() => handleProviderChange(p.id)}
                className="gn-adapters-provider-option__input"
              />
              <span className="gn-adapters-provider-option__name">
                {p.name}
              </span>
              <span className="gn-adapters-provider-option__desc">
                {p.description}
              </span>
              <span
                className={cx(
                  "gn-adapters-tier-badge",
                  `gn-adapters-tier-badge--${p.tier}`,
                )}
              >
                {p.tier}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      {needsApiKey && (
        <div className="gn-adapters-field">
          <label
            htmlFor={`gn-adapters-${domain}-apiKey`}
            className="gn-adapters-field__label"
          >
            API Key
          </label>
          <input
            id={`gn-adapters-${domain}-apiKey`}
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="gn-adapters-field__input"
            placeholder={`Enter ${selectedMeta?.name ?? provider} API key`}
            autoComplete="off"
            required
            aria-invalid={apiKeyHasError ? "true" : undefined}
            aria-describedby={apiKeyHasError ? errorId : undefined}
          />
        </div>
      )}
      {needsBaseUrl && (
        <div className="gn-adapters-field">
          <label
            htmlFor={`gn-adapters-${domain}-url`}
            className="gn-adapters-field__label"
          >
            Base URL
          </label>
          <input
            id={`gn-adapters-${domain}-url`}
            type="url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            className="gn-adapters-field__input"
            placeholder="https://..."
            required
            aria-invalid={baseUrlHasError ? "true" : undefined}
            aria-describedby={baseUrlHasError ? errorId : undefined}
          />
        </div>
      )}
      {optionFields.map((field) => {
        const hasFieldError = optionFieldHasError(field);
        const hintId = `gn-adapters-${domain}-${field.key}-hint`;
        const describedBy = hasFieldError ? `${errorId} ${hintId}` : hintId;
        return (
          <div key={field.key} className="gn-adapters-field">
            <label
              htmlFor={`gn-adapters-${domain}-${field.key}`}
              className="gn-adapters-field__label"
            >
              {field.label}
            </label>
            <input
              id={`gn-adapters-${domain}-${field.key}`}
              type="text"
              inputMode={field.inputMode}
              value={optionValues[field.key] ?? ""}
              onChange={(event) =>
                setOptionValues((current) => ({
                  ...current,
                  [field.key]: event.target.value,
                }))
              }
              className="gn-adapters-field__input"
              placeholder={field.placeholder}
              aria-invalid={hasFieldError ? "true" : undefined}
              aria-describedby={describedBy}
            />
            <p id={hintId} className="gn-adapters-field__hint">
              {field.description}
            </p>
          </div>
        );
      })}
      <div className="gn-adapters-actions">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="gn-adapters-btn gn-adapters-btn--ghost"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSaving || !provider}
          className="gn-adapters-btn gn-adapters-btn--primary"
        >
          {isSaving ? "Saving..." : "Save Configuration"}
        </button>
      </div>
    </form>
  );
}
